import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  Avatar,
  Chip,
  LinearProgress,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import { 
  MedicationOutlined as MedicationIcon, 
  CalendarToday as CalendarIcon,
  ArrowForward as ArrowForwardIcon,
  EventAvailable as EventAvailableIcon,
  EventBusy as EventBusyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingUpIcon,
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslations } from '../../translations/translations';
import { useAuth } from '../../auth/AuthContext';

// API URLs
const SLOTS_API_URL = 'https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/slots';

function Dashboard() {
  const theme = useTheme();
  const { language } = useLanguage();
  const t = useTranslations(language);
  const { user } = useAuth();
  
  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    slots: [],
    appointments: [],
    practiceData: {
      prescriptions: [],
      patients: []
    },
    doctorProfile: {
      firstName: '',
      lastName: '',
      specialty: '',
      title: 'Dr.'
    },
    stats: {
      availableSlots: 0,
      scheduledAppointments: 0,
      completedAppointments: 0,
      todayAppointments: 0,
      weekAppointments: 0,
      monthlyPatients: 0,
      pendingPrescriptions: 0,
      totalPatients: 0,
      availableSlotsDetails: [],
      monthlyCompletedAppointments: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch doctor profile information
  const fetchDoctorProfile = useCallback(async () => {
    if (!user?.username) return { firstName: '', lastName: '', specialty: '', title: 'Dr.' };
    
    try {
      const url = `https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/doctor?email=${encodeURIComponent(user.username)}`;
      console.log('Fetching doctor profile from:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Successfully fetched doctor profile:', data);
        return {
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          specialty: data.specialty || '',
          title: data.title || 'Dr.'
        };
      } else if (response.status === 404) {
        // Doctor profile doesn't exist yet, return default
        console.log('Doctor profile not found, using defaults');
        return {
          firstName: user.username || '',
          lastName: '',
          specialty: '',
          title: 'Dr.'
        };
      } else {
        console.error('Failed to fetch doctor profile:', response.status);
        return {
          firstName: user.username || '',
          lastName: '',
          specialty: '',
          title: 'Dr.'
        };
      }
    } catch (err) {
      console.error('Error fetching doctor profile:', err);
      return {
        firstName: user.username || '',
        lastName: '',
        specialty: '',
        title: 'Dr.'
      };
    }
  }, [user?.username]);

  // Fetch doctor slots
  const fetchDoctorSlots = useCallback(async () => {
    if (!user?.username) return [];
    
    try {
      const url = `${SLOTS_API_URL}?doctorId=${encodeURIComponent(user.username)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error('Failed to fetch slots');
      
      const data = await response.json();
      return data.items || [];
    } catch (err) {
      console.error('Error fetching slots:', err);
      return [];
    }
  }, [user?.username]);

  // Fetch real appointments using the same API pattern as Private Practice Hub
  const fetchAppointments = useCallback(async () => {
    if (!user?.username) return [];
    
    try {
      // Use the same API pattern as Private Practice Hub
      const fetchAppointmentsByStatus = async (statuses) => {
        const params = new URLSearchParams();
        params.append('assignedDoctorId', user.username);
        
        if (statuses.length > 0) {
          params.append('statuses', statuses.join(','));
        }
        
        params.append('includeUserData', 'true');
        
        const url = `https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/appointments?${params.toString()}`;
        console.log('Fetching appointments from:', url);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Raw appointments data:', data);
          return data.items || [];
        } else {
          console.error('Failed to fetch appointments:', response.status, response.statusText);
          return [];
        }
      };

      // Fetch all appointment types
      const [scheduled, confirmed, ongoing, completed] = await Promise.all([
        fetchAppointmentsByStatus(['SCHEDULED']),
        fetchAppointmentsByStatus(['CONFIRMED']), 
        fetchAppointmentsByStatus(['ONGOING']),
        fetchAppointmentsByStatus(['COMPLETED'])
      ]);

      // Combine all appointments
      const allAppointments = [...scheduled, ...confirmed, ...ongoing, ...completed];
      console.log('All appointments combined:', allAppointments);
      
      // Transform appointments to our expected format
      return allAppointments.map(apt => {
        // Debug logging to see the actual structure
        console.log('Processing appointment:', apt);
        console.log('User detail fields available:', Object.keys(apt.userDetail || {}));
        
        // More comprehensive patient name extraction
        let patientName = 'Unknown Patient';
        
        if (apt.userDetail) {
          // Try different field name variations
          const firstName = apt.userDetail.FirstName || apt.userDetail.firstName || apt.userDetail.first_name || '';
          const lastName = apt.userDetail.LastName || apt.userDetail.lastName || apt.userDetail.last_name || '';
          const fullName = `${firstName} ${lastName}`.trim();
          if (fullName) {
            patientName = fullName;
          } else if (apt.userDetail.name) {
            patientName = apt.userDetail.name;
          }
        } else if (apt.patientName) {
          patientName = apt.patientName;
        } else if (apt.patientDetails?.name) {
          patientName = apt.patientDetails.name;
        } else if (apt.patient?.name) {
          patientName = apt.patient.name;
        } else if (apt.userId) {
          patientName = `Patient ${apt.userId.slice(-8)}`;
        }

        console.log('Extracted patient name:', patientName);

        return {
          id: apt.appointmentId || apt.id || apt.requestId,
          patientName: patientName,
          status: apt.status || 'SCHEDULED',
          scheduledAt: apt.scheduledAt || apt.appointmentTime || apt.dateTime,
          confirmedSlot: apt.confirmedSlot,
          completedAt: apt.completedAt,
          duration: 20, // Fixed duration for 20-minute slots
          type: apt.appointmentType || apt.type || (language === 'pt' ? 'Consulta' : 'Consultation'),
          patientId: apt.patientId || apt.userId,
          notes: apt.notes,
          rawData: apt // Keep original data for debugging
        };
      });
      
    } catch (err) {
      console.error('Error fetching appointments:', err);
      return [];
    }
  }, [user?.username, language]);

  // Fetch additional practice data from other APIs
  const fetchPracticeData = useCallback(async () => {
    if (!user?.username) return { prescriptions: [], patients: [] };
    
    try {
      const practiceData = {
        prescriptions: [],
        patients: []
      };

      // Try to fetch chronic prescriptions
      try {
        const prescriptionsUrl = `https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/prescriptions?doctorId=${encodeURIComponent(user.username)}`;
        const prescriptionsResponse = await fetch(prescriptionsUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        if (prescriptionsResponse.ok) {
          const prescriptionsData = await prescriptionsResponse.json();
          practiceData.prescriptions = prescriptionsData.items || [];
        }
      } catch (err) {
        console.log('Prescriptions API not available');
      }

      // Try to fetch patients data
      try {
        const patientsUrl = `https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/patients?doctorId=${encodeURIComponent(user.username)}`;
        const patientsResponse = await fetch(patientsUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        if (patientsResponse.ok) {
          const patientsData = await patientsResponse.json();
          practiceData.patients = patientsData.items || [];
        }
      } catch (err) {
        console.log('Patients API not available');
      }

      return practiceData;
    } catch (err) {
      console.error('Error fetching practice data:', err);
      return { prescriptions: [], patients: [] };
    }
  }, [user?.username]);

  // Calculate dashboard statistics with proper data parsing
  const calculateStats = useCallback((slots, appointments, practiceData = { prescriptions: [], patients: [] }) => {
    console.log('Calculating stats with data:', { slots: slots.length, appointments: appointments.length });
    console.log('Sample slot:', slots[0]);
    console.log('Sample appointment:', appointments[0]);
    console.log('Appointment statuses:', appointments.map(apt => apt.status));
    
    const now = new Date();
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date(now);
    todayEnd.setHours(23, 59, 59, 999);
    
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay());
    weekStart.setHours(0, 0, 0, 0);
    
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    // Available slots - count future unbooked slots for this doctor
    const availableSlots = slots.filter(slot => {
      if (!slot.slot || !slot.assignedDoctorId) return false;
      if (slot.assignedDoctorId !== user?.username) return false;
      
      const slotDate = new Date(slot.slot);
      const isBooked = slot.booked === true || slot.booked === 'true';
      const isFuture = slotDate > now;
      
      return isFuture && !isBooked;
    }).length;

    // Scheduled appointments - count only appointments with SCHEDULED status
    const scheduledAppointments = appointments.filter(apt => {
      const status = (apt.status || '').toUpperCase();
      return status === 'SCHEDULED';
    }).length;

    // Completed appointments - count appointments that are completed
    const completedAppointments = appointments.filter(apt => {
      const status = (apt.status || '').toUpperCase();
      return status === 'COMPLETED';
    }).length;

    // Today's appointments - filter appointments for today (any status)
    const todayAppointments = appointments.filter(apt => {
      if (!apt.scheduledAt) return false;
      const aptDate = new Date(apt.scheduledAt);
      return aptDate >= todayStart && aptDate <= todayEnd;
    }).length;

    // This week's appointments - filter appointments for this week (any status)
    const weekAppointments = appointments.filter(apt => {
      if (!apt.scheduledAt) return false;
      const aptDate = new Date(apt.scheduledAt);
      return aptDate >= weekStart;
    }).length;

    // Get 3 closest days with available slots
    const availableSlotsDetails = slots
      .filter(slot => {
        if (!slot.slot || !slot.assignedDoctorId) return false;
        if (slot.assignedDoctorId !== user?.username) return false;
        
        const slotDate = new Date(slot.slot);
        const isBooked = slot.booked === true || slot.booked === 'true';
        const isFuture = slotDate > now;
        
        return isFuture && !isBooked;
      })
      .sort((a, b) => new Date(a.slot) - new Date(b.slot))
      .slice(0, 3)
      .map(slot => ({
        date: new Date(slot.slot).toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-US', {
          month: 'short',
          day: 'numeric'
        }),
        time: new Date(slot.slot).toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        })
      }));

    // Monthly unique patients from completed appointments this month
    let monthlyPatients = 0;
    if (practiceData.patients && practiceData.patients.length > 0) {
      // If we have patients API, still use completed appointments to filter
      const completedThisMonth = appointments.filter(apt => {
        if (!apt.scheduledAt) return false;
        const aptDate = new Date(apt.scheduledAt);
        const status = (apt.status || '').toUpperCase();
        return aptDate >= monthStart && status === 'COMPLETED';
      });
      
      monthlyPatients = new Set(completedThisMonth.map(apt => apt.patientName)).size;
    } else {
      // Fallback: count unique patients from completed appointments this month
      monthlyPatients = new Set(
        appointments
          .filter(apt => {
            if (!apt.scheduledAt) return false;
            const aptDate = new Date(apt.scheduledAt);
            const status = (apt.status || '').toUpperCase();
            return aptDate >= monthStart && status === 'COMPLETED';
          })
          .map(apt => apt.patientName)
      ).size;
    }

    // Monthly completed appointments count
    const monthlyCompletedAppointments = appointments.filter(apt => {
      if (!apt.scheduledAt) return false;
      const aptDate = new Date(apt.scheduledAt);
      const status = (apt.status || '').toUpperCase();
      return aptDate >= monthStart && status === 'COMPLETED';
    }).length;

    // Pending prescriptions count
    const pendingPrescriptions = practiceData.prescriptions.filter(prescription => 
      prescription.status === 'PENDING' || prescription.status === 'DRAFT'
    ).length;

    // Total patients count
    const totalPatients = practiceData.patients.length || new Set(appointments.map(apt => apt.patientName)).size;

    const stats = {
      availableSlots,
      scheduledAppointments,
      completedAppointments,
      todayAppointments,
      weekAppointments,
      monthlyPatients,
      pendingPrescriptions,
      totalPatients,
      availableSlotsDetails,
      monthlyCompletedAppointments
    };

    console.log('Calculated stats:', stats);
    return stats;
  }, [user?.username, language]);

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [slots, appointments, practiceData, doctorProfile] = await Promise.all([
        fetchDoctorSlots(),
        fetchAppointments(),
        fetchPracticeData(),
        fetchDoctorProfile()
      ]);

      const stats = calculateStats(slots, appointments, practiceData);

      setDashboardData({
        slots,
        appointments,
        practiceData,
        doctorProfile,
        stats
      });
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.username, fetchDoctorSlots, fetchAppointments, fetchPracticeData, fetchDoctorProfile, calculateStats]);

  // Load data on component mount
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Get upcoming appointments for today
  const getTodayAppointments = () => {
    const now = new Date();
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date(now);
    todayEnd.setHours(23, 59, 59, 999);

    return dashboardData.appointments
      .filter(apt => {
        const aptDate = new Date(apt.scheduledAt);
        return aptDate >= todayStart && aptDate <= todayEnd;
      })
      .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
  };

  // Get next few appointments - using same logic as Scheduled Appointments statistic
  const getUpcomingAppointments = () => {
    // Use the exact same filter logic as the scheduled appointments statistic
    const scheduledAppointments = dashboardData.appointments.filter(apt => {
      const status = (apt.status || '').toUpperCase();
      return status === 'SCHEDULED';
    });

    // Transform and sort the scheduled appointments for display
    return scheduledAppointments
      .map(appointment => ({
        ...appointment,
        // Extract patient name from userDetail or use fallback
        patientName: appointment.userDetail ? 
          `${appointment.userDetail.FirstName || appointment.userDetail.firstName || ''} ${appointment.userDetail.LastName || appointment.userDetail.lastName || ''}`.trim() :
          (appointment.patientName || `Patient ${appointment.userId?.slice(-8) || 'Unknown'}`),
        // Use confirmedSlot or requestedAt for display
        displayDate: appointment.confirmedSlot || appointment.requestedAt || appointment.scheduledAt
      }))
      .filter(apt => apt.displayDate) // Only show appointments with valid dates
      .sort((a, b) => new Date(a.displayDate) - new Date(b.displayDate))
      .slice(0, 5);
  };

  // Format time for display
  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString(language === 'pt' ? 'pt-PT' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={loadDashboardData}
              startIcon={<RefreshIcon />}
            >
              {t.common.retry}
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  const todayAppointments = getTodayAppointments();
  const upcomingAppointments = getUpcomingAppointments();

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mt: 0 }}>
            {language === 'pt' ? 'Bom dia' : 'Good morning'}{dashboardData.doctorProfile.firstName ? `, ${dashboardData.doctorProfile.title} ${dashboardData.doctorProfile.firstName} ${dashboardData.doctorProfile.lastName}` : ''} 👋
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
            {language === 'pt' ? 'Resumo da sua prática médica' : 'Your medical practice overview'}
            {dashboardData.doctorProfile.specialty && (
              <span> • {dashboardData.doctorProfile.specialty}</span>
            )}
        </Typography>
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={loadDashboardData}
          disabled={loading}
          sx={{ 
            borderRadius: 8,
            px: 2,
            py: 1,
            borderColor: alpha(theme.palette.primary.main, 0.5),
            '&:hover': {
              borderColor: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.04)
            }
          }}
        >
          {language === 'pt' ? 'Atualizar' : 'Refresh'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Key Statistics Cards */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {/* Available Slots */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.15)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: alpha(theme.palette.success.main, 0.2), 
                        color: theme.palette.success.main,
                        width: 48,
                        height: 48
                      }}
                    >
                      <EventAvailableIcon />
                    </Avatar>
                  </Box>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 700, mb: 0.5, color: theme.palette.success.main }}>
                    {dashboardData.stats.availableSlots}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    {language === 'pt' ? 'Horários Disponíveis' : 'Available Slots'}
                  </Typography>
                  {dashboardData.stats.availableSlotsDetails.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 600, display: 'block', mb: 0.5 }}>
                        {language === 'pt' ? 'Próximos:' : 'Next:'}
                      </Typography>
                      {dashboardData.stats.availableSlotsDetails.map((slot, index) => (
                        <Typography key={index} variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                          {slot.date} • {slot.time}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  <Button 
                    component={Link} 
                    to="/profile"
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ 
                      mt: 1, 
                      fontWeight: 600, 
                      fontSize: '0.75rem',
                      color: theme.palette.success.main,
                      p: 0,
                      '&:hover': {
                        bgcolor: 'transparent',
                        color: theme.palette.success.dark
                      }
                    }}
                  >
                    {language === 'pt' ? 'Gerenciar' : 'Manage'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Scheduled Appointments */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: alpha(theme.palette.primary.main, 0.2), 
                        color: theme.palette.primary.main,
                        width: 48,
                        height: 48
                      }}
                    >
                      <EventBusyIcon />
                    </Avatar>
                  </Box>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 700, mb: 0.5, color: theme.palette.primary.main }}>
                    {dashboardData.stats.scheduledAppointments}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    {language === 'pt' ? 'Consultas Agendadas' : 'Scheduled Appointments'}
                  </Typography>
                  <Button 
                    component={Link} 
                    to="/private-practice-hub"
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ 
                      mt: 1, 
                      fontWeight: 600, 
                      fontSize: '0.75rem',
                      color: theme.palette.primary.main,
                      p: 0,
                      '&:hover': {
                        bgcolor: 'transparent',
                        color: theme.palette.primary.dark
                      }
                    }}
                  >
                    {language === 'pt' ? 'Ver Todas' : 'View All'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Today's Appointments */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.05)} 0%, ${alpha(theme.palette.warning.main, 0.15)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: alpha(theme.palette.warning.main, 0.2), 
                        color: theme.palette.warning.main,
                        width: 48,
                        height: 48
                      }}
                    >
                      <ScheduleIcon />
                    </Avatar>
                  </Box>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 700, mb: 0.5, color: theme.palette.warning.main }}>
                    {dashboardData.stats.todayAppointments}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    {language === 'pt' ? 'Consultas Hoje' : "Today's Appointments"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.warning.main, fontWeight: 600 }}>
                    {formatDate(new Date().toISOString())}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* This Week Appointments */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: alpha(theme.palette.secondary.main, 0.2), 
                        color: theme.palette.secondary.main,
                        width: 48,
                        height: 48
                      }}
                    >
                      <CalendarIcon />
                    </Avatar>
                  </Box>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 700, mb: 0.5, color: theme.palette.secondary.main }}>
                    {dashboardData.stats.weekAppointments}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    {language === 'pt' ? 'Consultas Esta Semana' : 'Appointments This Week'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarIcon sx={{ fontSize: 16, color: theme.palette.secondary.main, mr: 0.5 }} />
                    <Typography variant="caption" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>
                      {language === 'pt' ? 'Semana atual' : 'Current week'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Additional Practice Statistics */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {/* Pending Prescriptions */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                  sx={{ 
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${alpha('#ff9800', 0.05)} 0%, ${alpha('#ff9800', 0.15)} 100%)`,
                  border: `1px solid ${alpha('#ff9800', 0.2)}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: alpha('#ff9800', 0.2), 
                        color: '#ff9800',
                        width: 48,
                        height: 48
                      }}
                    >
                      <MedicationIcon />
                    </Avatar>
                  </Box>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 700, mb: 0.5, color: '#ff9800' }}>
                    {dashboardData.stats.pendingPrescriptions}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    {language === 'pt' ? 'Prescrições Pendentes' : 'Pending Prescriptions'}
                  </Typography>
                  <Button 
                    component={Link} 
                    to="/chronic-prescriptions"
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ 
                      mt: 1, 
                      fontWeight: 600, 
                      fontSize: '0.75rem',
                      color: '#ff9800',
                      p: 0,
                      '&:hover': {
                        bgcolor: 'transparent',
                        color: '#f57c00'
                      }
                    }}
                  >
                    {language === 'pt' ? 'Revisar' : 'Review'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Total Patients */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${alpha('#9c27b0', 0.05)} 0%, ${alpha('#9c27b0', 0.15)} 100%)`,
                  border: `1px solid ${alpha('#9c27b0', 0.2)}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: alpha('#9c27b0', 0.2), 
                        color: '#9c27b0',
                        width: 48,
                        height: 48
                      }}
                    >
                      <PersonAddIcon />
                    </Avatar>
                  </Box>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 700, mb: 0.5, color: '#9c27b0' }}>
                    {dashboardData.stats.totalPatients}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    {language === 'pt' ? 'Total de Pacientes' : 'Total Patients'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#9c27b0', fontWeight: 600 }}>
                    {language === 'pt' ? 'Na sua prática' : 'In your practice'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Appointment Rate */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                  sx={{ 
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${alpha('#3f51b5', 0.05)} 0%, ${alpha('#3f51b5', 0.15)} 100%)`,
                  border: `1px solid ${alpha('#3f51b5', 0.2)}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: alpha('#3f51b5', 0.2), 
                        color: '#3f51b5',
                        width: 48,
                        height: 48
                      }}
                    >
                      <TrendingUpIcon />
                    </Avatar>
                  </Box>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 700, mb: 0.5, color: '#3f51b5' }}>
                    {dashboardData.stats.monthlyPatients}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    {language === 'pt' ? 'Pacientes Únicos Este Mês' : 'Unique Patients This Month'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#3f51b5', fontWeight: 600 }}>
                    {language === 'pt' ? 'De consultas concluídas' : 'From completed appointments'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Completed Appointments */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                    sx={{ 
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${alpha('#00796b', 0.05)} 0%, ${alpha('#00796b', 0.15)} 100%)`,
                  border: `1px solid ${alpha('#00796b', 0.2)}`,
                  transition: 'transform 0.2s',
                      '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: alpha('#00796b', 0.2), 
                        color: '#00796b',
                        width: 48,
                        height: 48
                      }}
                    >
                      <CheckCircleIcon />
                    </Avatar>
                  </Box>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 700, mb: 0.5, color: '#00796b' }}>
                    {dashboardData.stats.completedAppointments}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    {language === 'pt' ? 'Consultas Concluídas' : 'Completed Appointments'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#00796b', fontWeight: 600 }}>
                    {language === 'pt' ? 'Total concluído' : 'Total completed'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Today's Schedule */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '400px', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <CalendarIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {language === 'pt' ? 'Agenda de Hoje' : "Today's Schedule"}
              </Typography>
              <Chip 
                label={`${todayAppointments.length} ${language === 'pt' ? 'consultas' : 'appointments'}`}
                size="small"
                color="primary"
                sx={{ ml: 2 }}
              />
            </Box>
            
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {todayAppointments.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4 }}>
                  <CalendarIcon sx={{ fontSize: 64, color: theme.palette.grey[300], mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    {language === 'pt' ? 'Sem consultas hoje' : 'No appointments today'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {language === 'pt' ? 'Aproveite para descansar ou gerir a sua disponibilidade' : 'Take a break or manage your availability'}
                </Typography>
              </Box>
              ) : (
                <List sx={{ py: 0 }}>
                  {todayAppointments.map((appointment, index) => (
                  <React.Fragment key={appointment.id}>
                      <ListItem sx={{ px: 0, py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Box sx={{ mr: 3, textAlign: 'center', minWidth: 60 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>
                              {formatTime(appointment.scheduledAt)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {appointment.duration}min
                            </Typography>
                          </Box>
                          
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {appointment.patientName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {appointment.type}
                            </Typography>
                            <Chip 
                              label={appointment.status}
                              size="small" 
                              color={
                                appointment.status === 'COMPLETED' ? 'success' :
                                appointment.status === 'CONFIRMED' ? 'primary' : 'warning'
                              }
                              icon={
                                appointment.status === 'COMPLETED' ? <CheckCircleIcon /> :
                                appointment.status === 'CONFIRMED' ? <ScheduleIcon /> : <PendingIcon />
                              }
                            />
                          </Box>
                          
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                              €{appointment.revenue}
                            </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                      {index < todayAppointments.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Practice Overview */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ 
            p: { xs: 2, sm: 3 }, 
            borderRadius: 2, 
            height: { xs: 'auto', md: '400px' }, 
            display: 'flex', 
            flexDirection: 'column'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: { xs: 2, sm: 3 },
              flexWrap: 'wrap'
            }}>
              <TrendingUpIcon sx={{ mr: 1, color: theme.palette.secondary.main }} />
              <Typography variant="h6" sx={{ 
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                {language === 'pt' ? 'Resumo da Prática' : 'Practice Overview'}
              </Typography>
            </Box>
            
            {/* Scheduled Appointments */}
            <Box sx={{ mb: { xs: 2, sm: 3 } }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 1,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                gap: { xs: 1, sm: 0 }
              }}>
                <Typography variant="body2" color="text.secondary" sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}>
                  {language === 'pt' ? 'Consultas Agendadas' : 'Scheduled Appointments'}
                </Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}>
                  {dashboardData.stats.scheduledAppointments}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={Math.min((dashboardData.stats.scheduledAppointments / 20) * 100, 100)}
                sx={{ 
                  height: { xs: 6, sm: 8 }, 
                  borderRadius: 4 
                }}
              />
            </Box>

             {/* Monthly Completed Appointments */}
             <Box sx={{ mb: { xs: 2, sm: 3 } }}>
               <Box sx={{ 
                 display: 'flex', 
                 justifyContent: 'space-between', 
                 alignItems: 'center', 
                 mb: 1,
                 flexWrap: { xs: 'wrap', sm: 'nowrap' },
                 gap: { xs: 1, sm: 0 }
               }}>
                 <Typography variant="body2" color="text.secondary" sx={{
                   fontSize: { xs: '0.75rem', sm: '0.875rem' }
                 }}>
                   {language === 'pt' ? 'Consultas Concluídas Este Mês' : 'Appointments Completed This Month'}
                 </Typography>
                 <Typography variant="h6" sx={{ 
                   fontWeight: 700,
                   fontSize: { xs: '1rem', sm: '1.25rem' }
                 }}>
                   {dashboardData.stats.monthlyCompletedAppointments}
                 </Typography>
               </Box>
               <LinearProgress 
                 variant="determinate" 
                 value={Math.min((dashboardData.stats.monthlyCompletedAppointments / 30) * 100, 100)}
                 color="secondary"
                 sx={{ 
                   height: { xs: 6, sm: 8 }, 
                   borderRadius: 4 
                 }}
               />
             </Box>

            {/* Next Appointments */}
            <Typography variant="subtitle2" sx={{ 
              fontWeight: 600, 
              mb: { xs: 1, sm: 2 },
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}>
              {language === 'pt' ? 'Próximas Consultas' : 'Upcoming Appointments'}
            </Typography>
            
            {/* Appointments list with flexible height */}
            <Box sx={{ flexGrow: 1, mb: { xs: 1, sm: 2 } }}>
              {upcomingAppointments.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: { xs: 1, sm: 2 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: '60px'
                }}>
                  <Typography variant="body2" color="text.secondary" sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    {language === 'pt' ? 'Nenhuma consulta agendada' : 'No upcoming appointments'}
                  </Typography>
                </Box>
              ) : (
                <List sx={{ py: 0 }}>
                  {upcomingAppointments.slice(0, 3).map((appointment) => (
                    <ListItem key={appointment.id} sx={{ 
                      px: 0, 
                      py: { xs: 0.5, sm: 1 }
                    }}>
                      <ListItemText
                        primary={appointment.patientName}
                        secondary={`${formatDate(appointment.displayDate)} • ${formatTime(appointment.displayDate)}`}
                        primaryTypographyProps={{ 
                          variant: 'body2', 
                          fontWeight: 600,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                        secondaryTypographyProps={{ 
                          variant: 'caption',
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            {/* Button at bottom */}
            <Button 
              component={Link} 
              to="/private-practice-hub"
              variant="outlined"
              size="small"
              fullWidth
              sx={{ 
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                py: { xs: 0.5, sm: 1 }
              }}
            >
              {language === 'pt' ? 'Ver Todas as Consultas' : 'View All Appointments'}
            </Button>
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              {language === 'pt' ? 'Ações Rápidas' : 'Quick Actions'}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  component={Link}
                  to="/profile"
                  variant="outlined"
                  fullWidth
                  startIcon={<EventAvailableIcon />}
                  sx={{ 
                    py: 1.5,
                    justifyContent: 'flex-start',
                    borderColor: alpha(theme.palette.success.main, 0.5),
                    color: theme.palette.success.main,
                    '&:hover': {
                      borderColor: theme.palette.success.main,
                      bgcolor: alpha(theme.palette.success.main, 0.04)
                    }
                  }}
                >
                  {language === 'pt' ? 'Gerir Disponibilidade' : 'Manage Availability'}
                </Button>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  component={Link}
                  to="/private-practice-hub"
                  variant="outlined"
                  fullWidth
                  startIcon={<CalendarIcon />}
                  sx={{ py: 1.5, justifyContent: 'flex-start' }}
                >
                  {language === 'pt' ? 'Ver Agenda' : 'View Calendar'}
                </Button>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  component={Link}
                  to="/chronic-prescriptions"
                  variant="outlined"
                  fullWidth
                  startIcon={<MedicationIcon />}
                  sx={{ py: 1.5, justifyContent: 'flex-start' }}
                >
                  {language === 'pt' ? 'Prescrições' : 'Prescriptions'}
                </Button>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<PersonAddIcon />}
                  sx={{ 
                    py: 1.5, 
                    justifyContent: 'flex-start',
                    borderColor: alpha(theme.palette.secondary.main, 0.5),
                    color: theme.palette.secondary.main,
                    '&:hover': {
                      borderColor: theme.palette.secondary.main,
                      bgcolor: alpha(theme.palette.secondary.main, 0.04)
                    }
                  }}
                >
                  {language === 'pt' ? 'Novo Paciente' : 'New Patient'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard; 